import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';
import { Navigate, Link } from 'react-router-dom';
import { Mail, Send, AlertTriangle, Loader2, Check, Users, TrendingUp } from 'lucide-react';
import { API } from '../utils/apiBase';


const SEGMENTS = [
  { id: 'all_users', label: 'All Users', note: 'Every verified user' },
  { id: 'free_users', label: 'Free Tier', note: 'Users on the free plan' },
  { id: 'pro_users', label: 'Paid (Basic + Pro)', note: 'Monetized users' },
  { id: 'inactive_users', label: 'Inactive (30d+)', note: 'Not active recently' },
  { id: 'leads_only', label: 'Leads Only', note: 'Free-ATS leads who have not signed up' },
];

const TEMPLATES = [
  {
    id: 'blank',
    label: 'Blank',
    subject: '',
    html: '',
  },
  {
    id: 'feature_launch',
    label: 'Feature Launch',
    subject: "We just shipped something you're going to love",
    html: `<h2 style="color:#002fa7;">New on Ascend</h2>
<p>We've rolled out a new feature we think you'll love — and we built it because of feedback from users like you.</p>
<p><strong>What's new:</strong> [describe the feature here].</p>
<p><a href="https://ascendforyou.net/dashboard" style="display:inline-block;background:#002fa7;color:#fff;padding:12px 24px;text-decoration:none;font-weight:700;">Try it now</a></p>`,
  },
  {
    id: 'upgrade_promo',
    label: 'Upgrade Promo',
    subject: '48 hours: 30% off Ascend Pro',
    html: `<h2 style="color:#002fa7;">Unlock Pro — 30% off for 48h</h2>
<p>Pro includes unlimited resume optimizations, AI cover letters, LinkedIn rewrites, and AI Career Coach access.</p>
<p><a href="https://ascendforyou.net/pricing" style="display:inline-block;background:#002fa7;color:#fff;padding:12px 24px;text-decoration:none;font-weight:700;">Upgrade now</a></p>`,
  },
  {
    id: 'reengage',
    label: 'Re-engagement',
    subject: "Your resume is waiting for you",
    html: `<h2 style="color:#002fa7;">Haven't optimized lately?</h2>
<p>Recruiters move fast. A resume that scored a 72 last month might already be behind keyword trends.</p>
<p>Come back in, run a fresh analysis, and see what's changed.</p>
<p><a href="https://ascendforyou.net/dashboard" style="display:inline-block;background:#002fa7;color:#fff;padding:12px 24px;text-decoration:none;font-weight:700;">Re-score my resume</a></p>`,
  },
];

export default function AdminBroadcast() {
  useHideCookieBanner();
  const { user, loading: authLoading } = useAuth();
  const [segmentCounts, setSegmentCounts] = useState({});
  const [segment, setSegment] = useState('leads_only');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [testOnly, setTestOnly] = useState(true);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    async function loadSegments() {
      try {
        const res = await axios.get(`${API}/admin/broadcast/segments`, { withCredentials: true });
        setSegmentCounts(res.data.segments || {});
      } catch {
        // silent
      }
    }
    loadSegments();
  }, []);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject);
    setHtml(tpl.html);
  };

  const send = async () => {
    if (!subject.trim() || !html.trim()) {
      setErr('Subject and body are both required.');
      return;
    }
    if (!testOnly && !window.confirm(`This will email ${segmentCounts[segment] ?? '?'} people. Proceed?`)) return;
    setSending(true);
    setErr('');
    setResult(null);
    try {
      const res = await axios.post(`${API}/admin/broadcast/send`, {
        segment, subject, html, test_only: testOnly,
      }, { withCredentials: true });
      setResult(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading...</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const totalRecipients = testOnly ? 1 : (segmentCounts[segment] || 0);

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-broadcast-page">
      <AdminReadinessBanner />
      <AdminSubNav />
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">ADMIN</span>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115]">Broadcast Email</h1>
            <p className="text-sm text-[#6b7280]">Send a segmented email blast via Resend.</p>
          </div>
          <Link to="/admin/funnel" className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-2" data-testid="link-funnel">
            <TrendingUp size={14} /> Funnel
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: segment + template */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white border border-[#e5e7eb] p-5">
              <h2 className="font-satoshi text-sm uppercase tracking-wider font-bold text-[#6b7280] mb-3 flex items-center gap-2">
                <Users size={14} /> Segment
              </h2>
              <div className="space-y-2" data-testid="segment-list">
                {SEGMENTS.map(s => (
                  <label key={s.id} className={`flex items-start gap-2 p-3 border cursor-pointer transition-colors ${segment === s.id ? 'border-[#002fa7] bg-[#002fa7]/5' : 'border-[#e5e7eb] hover:border-[#002fa7]/50'}`}>
                    <input
                      type="radio"
                      name="segment"
                      checked={segment === s.id}
                      onChange={() => setSegment(s.id)}
                      className="mt-0.5"
                      data-testid={`segment-${s.id}`}
                    />
                    <div className="flex-1 text-sm">
                      <div className="flex justify-between">
                        <span className="font-semibold text-[#0f1115]">{s.label}</span>
                        <span className="text-[#002fa7] font-mono text-xs">{segmentCounts[s.id] ?? '…'}</span>
                      </div>
                      <div className="text-xs text-[#6b7280]">{s.note}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#e5e7eb] p-5">
              <h2 className="font-satoshi text-sm uppercase tracking-wider font-bold text-[#6b7280] mb-3">Templates</h2>
              <div className="space-y-2" data-testid="template-list">
                {TEMPLATES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className="w-full text-left p-2 text-sm border border-[#e5e7eb] hover:border-[#002fa7] hover:bg-[#f4f5f7] transition-colors"
                    data-testid={`template-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: compose */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white border border-[#e5e7eb] p-5">
              <label className="block text-xs uppercase tracking-wider font-bold text-[#6b7280] mb-2">Subject</label>
              <input
                type="text" value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Your subject line"
                className="w-full px-3 py-2 border border-[#e5e7eb] text-sm focus:outline-none focus:border-[#002fa7]"
                data-testid="broadcast-subject"
              />

              <label className="block text-xs uppercase tracking-wider font-bold text-[#6b7280] mt-4 mb-2">HTML Body</label>
              <textarea
                rows={12} value={html}
                onChange={(e) => setHtml(e.target.value)}
                placeholder="<h2>Headline</h2><p>Body text...</p>"
                className="w-full px-3 py-2 border border-[#e5e7eb] text-sm font-mono focus:outline-none focus:border-[#002fa7]"
                data-testid="broadcast-html"
              />
              <p className="text-xs text-[#6b7280] mt-2">
                An unsubscribe link and Ascend footer are automatically appended.
              </p>
            </div>

            <div className="bg-white border border-[#e5e7eb] p-5">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox" id="test_only" checked={testOnly}
                  onChange={(e) => setTestOnly(e.target.checked)}
                  data-testid="test-only-toggle"
                />
                <label htmlFor="test_only" className="text-sm text-[#0f1115] font-semibold">
                  Test-send to my admin email only
                </label>
              </div>
              <p className="text-xs text-[#6b7280] mb-4">
                {testOnly
                  ? `This will send 1 email to your admin inbox for preview. Uncheck to send to all ${segmentCounts[segment] || 0} recipients in "${segment}".`
                  : `This will send to ${segmentCounts[segment] || 0} recipient(s) in "${segment}".`}
              </p>

              {!testOnly && (segmentCounts[segment] || 0) > 0 && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-2" data-testid="broadcast-recipient-warning">
                  <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>You are about to email <strong>{(segmentCounts[segment] || 0).toLocaleString()}</strong> real inboxes. Each one counts against the Resend free-plan limit (100/day, 3,000/month).</span>
                </div>
              )}

              <button
                onClick={send}
                disabled={sending || !subject.trim() || !html.trim()}
                className="px-5 py-3 bg-[#002fa7] text-white font-bold hover:bg-[#001d6a] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="broadcast-send-btn"
              >
                {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={16} /> {testOnly ? 'Send test' : `Send to ${totalRecipients}`}</>}
              </button>

              {err && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm flex items-center gap-2" data-testid="broadcast-error">
                  <AlertTriangle size={14} /> {err}
                </div>
              )}
              {result && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 text-green-800 text-sm flex items-center gap-2" data-testid="broadcast-result">
                  <Check size={14} /> Sent {result.sent} · Failed {result.failed} · Recipients {result.recipients}
                </div>
              )}
            </div>

            <div className="bg-white border border-[#e5e7eb] p-5">
              <h3 className="font-satoshi text-sm uppercase tracking-wider font-bold text-[#6b7280] mb-2 flex items-center gap-2"><Mail size={14} /> Preview</h3>
              <div className="border border-[#e5e7eb] p-4 text-sm text-[#0f1115]" data-testid="broadcast-preview" dangerouslySetInnerHTML={{ __html: html || '<em style="color:#9ca3af;">Body preview will appear here.</em>' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
